import { OptionType } from '@evinced-private/ui-common';

import { UNIT_ID_LABEL_KEY } from 'src/common/consts/platformConfigConsts';
import { capitalizeFirstLetter, lowercaseFirstLetter } from 'src/common/helpers/FormatHelper';
import { buildTupleString } from 'src/common/helpers/GQLRequestsHelper/buildTupleString';
import {
	ConditionOperators,
	ConditionString,
	ConditionStringArray
} from 'src/common/types/graphql.generated';

// Label name constants for exact matching
export const ENVIRONMENT_FILTER_NAME = 'Environment';
export const FLOW_FILTER_NAME = 'Flow';

// Label name constants for exact matching (lowercase for comparison)
const UNIT_ID_LABEL = UNIT_ID_LABEL_KEY.toLowerCase();
const ENVIRONMENT_LABEL = ENVIRONMENT_FILTER_NAME.toLowerCase();
const FLOW_LABEL = FLOW_FILTER_NAME.toLowerCase();

interface ParsedLabelFilters {
	environmentValues: string[];
	flowValues: string[];
	environmentCustomFieldTuples: string[][];
	flowCustomFieldTuples: string[][];
	unitIdCustomFieldTuples: string[][];
	otherCustomFieldTuples: string[][];
}

/**
 * Adds a tuple string to a Map, grouping tuples by their key
 */
const addTupleToMap = (map: Map<string, string[]>, key: string, tupleString: string): void => {
	if (!map.has(key)) {
		map.set(key, []);
	}
	map.get(key)?.push(tupleString);
};

/**
 * Converts a Map of grouped tuples to an array of arrays
 * Each Map entry becomes one inner array containing all tuples for that key
 */
const mapToTuplesArray = (map: Map<string, string[]>): string[][] => Array.from(map.values());

/**
 * Flattens all Map values into a single array wrapped in an outer array
 * This creates OR behavior between all values regardless of key
 */
const flattenMapToSingleGroup = (map: Map<string, string[]>): string[][] => {
	const allValues: string[] = [];
	map.forEach((values) => allValues.push(...values));
	return allValues.length > 0 ? [allValues] : [];
};

/**
 * Processes a single label and adds it to the appropriate filter collections
 *
 * Label categorization logic:
 * 1. Direct labels: "Environment: <value>" or "Flow: <value>" (exact case match)
 *    - Used for both direct field filtering AND custom field matching
 * 2. Custom fields: Any other "Key: Value" format
 *    - Case-insensitive key matching for special keys (unitId, environment, flow)
 *    - All other keys go to "other" category
 */
const processLabel = (
	label: OptionType,
	collections: {
		environmentValues: string[];
		flowValues: string[];
		environmentCustomFieldTuplesMap: Map<string, string[]>;
		flowCustomFieldTuplesMap: Map<string, string[]>;
		unitIdCustomFieldTuples: string[];
		otherCustomFieldTuplesMap: Map<string, string[]>;
	}
): void => {
	const labelValueStr = label.value as string;
	const labelParts = labelValueStr.split(': ');

	// Skip invalid label format (must have exactly one ': ' separator)
	if (labelParts.length !== 2) {
		return;
	}

	const [labelName, labelValue] = labelParts;
	const labelNameLower = labelName.toLowerCase();
	const normalizedKey = lowercaseFirstLetter(labelName);
	const tupleString = buildTupleString(normalizedKey, labelValue);

	// Handle direct "Environment: <value>" labels (exact case match)
	// Adds to both direct environment values and custom field tuples
	if (labelName === ENVIRONMENT_FILTER_NAME) {
		const capitalizedValue = capitalizeFirstLetter(labelValue);
		const normalizedValue = labelValue.toLowerCase();

		collections.environmentValues.push(capitalizedValue);
		if (normalizedValue !== capitalizedValue) {
			collections.environmentValues.push(normalizedValue);
		}

		const originalTupleString = buildTupleString(normalizedKey, labelValue);
		const normalizedTupleString = buildTupleString(normalizedKey, normalizedValue);

		addTupleToMap(collections.environmentCustomFieldTuplesMap, normalizedKey, originalTupleString);
		if (normalizedValue !== labelValue) {
			addTupleToMap(
				collections.environmentCustomFieldTuplesMap,
				normalizedKey,
				normalizedTupleString
			);
		}
		return;
	}

	// Handle direct "Flow: <value>" labels (exact case match)
	// Adds to both direct flow values and custom field tuples
	if (labelName === FLOW_FILTER_NAME) {
		collections.flowValues.push(labelValue.toLowerCase());
		addTupleToMap(collections.flowCustomFieldTuplesMap, normalizedKey, tupleString);
		return;
	}

	// Handle custom field labels (case-insensitive matching)
	if (labelNameLower === UNIT_ID_LABEL) {
		const originalTupleString = buildTupleString(normalizedKey, labelValue);
		const normalizedValue = labelValue.toLowerCase();
		const normalizedTupleString = buildTupleString(normalizedKey, normalizedValue);

		collections.unitIdCustomFieldTuples.push(originalTupleString);
		if (normalizedValue !== labelValue) {
			collections.unitIdCustomFieldTuples.push(normalizedTupleString);
		}
		return;
	}

	if (labelNameLower === ENVIRONMENT_LABEL) {
		const originalTupleString = buildTupleString(normalizedKey, labelValue);
		const normalizedValue = labelValue.toLowerCase();
		const normalizedTupleString = buildTupleString(normalizedKey, normalizedValue);

		addTupleToMap(collections.environmentCustomFieldTuplesMap, normalizedKey, originalTupleString);
		if (normalizedValue !== labelValue) {
			addTupleToMap(
				collections.environmentCustomFieldTuplesMap,
				normalizedKey,
				normalizedTupleString
			);
		}
		return;
	}

	if (labelNameLower === FLOW_LABEL) {
		addTupleToMap(collections.flowCustomFieldTuplesMap, normalizedKey, tupleString);
		return;
	}

	// All other custom fields
	addTupleToMap(collections.otherCustomFieldTuplesMap, normalizedKey, tupleString);
};

/**
 * Parses labels into separate filter categories for GraphQL queries
 */
export const parseLabelsIntoFilters = (labels: OptionType[]): ParsedLabelFilters => {
	const collections = {
		environmentValues: [] as string[],
		flowValues: [] as string[],
		environmentCustomFieldTuplesMap: new Map<string, string[]>(),
		flowCustomFieldTuplesMap: new Map<string, string[]>(),
		unitIdCustomFieldTuples: [] as string[],
		otherCustomFieldTuplesMap: new Map<string, string[]>()
	};

	labels.forEach((label) => processLabel(label, collections));

	return {
		environmentValues: collections.environmentValues,
		flowValues: collections.flowValues,
		environmentCustomFieldTuples: mapToTuplesArray(collections.environmentCustomFieldTuplesMap),
		flowCustomFieldTuples: mapToTuplesArray(collections.flowCustomFieldTuplesMap),
		unitIdCustomFieldTuples:
			collections.unitIdCustomFieldTuples.length > 0 ? [collections.unitIdCustomFieldTuples] : [],
		// Flatten other custom fields into single group for OR behavior between all values
		otherCustomFieldTuples: flattenMapToSingleGroup(collections.otherCustomFieldTuplesMap)
	};
};

/**
 * Builds a ConditionStringArray from grouped tuples
 */
const buildConditionStringArray = (
	tuples: string[][],
	operator: ConditionOperators = ConditionOperators.All
): ConditionStringArray | undefined => {
	if (tuples.length === 0) {
		return undefined;
	}
	return {
		value: tuples,
		operator
	};
};

/**
 * Builds a ConditionString from an array of values
 */
const buildConditionString = (values: string[]): ConditionString | undefined => {
	if (values.length === 0) {
		return undefined;
	}
	return {
		value: values
	};
};

/**
 * Builds all filter variables from parsed label filters
 */
export const buildFilterVariables = (
	parsedFilters: ParsedLabelFilters
): {
	environmentCustomFieldsFilter?: ConditionStringArray;
	environmentsFilter?: ConditionString;
	flowsFilter?: ConditionString;
	flowsCustomFieldsFilter?: ConditionStringArray;
	unitIdCustomFieldsFilter?: ConditionStringArray;
	otherCustomFieldsFilter?: ConditionStringArray;
} => {
	return {
		environmentCustomFieldsFilter: buildConditionStringArray(
			parsedFilters.environmentCustomFieldTuples,
			ConditionOperators.All
		),
		environmentsFilter: buildConditionString(parsedFilters.environmentValues),
		flowsFilter: buildConditionString(parsedFilters.flowValues),
		flowsCustomFieldsFilter: buildConditionStringArray(
			parsedFilters.flowCustomFieldTuples,
			ConditionOperators.All
		),
		unitIdCustomFieldsFilter: buildConditionStringArray(
			parsedFilters.unitIdCustomFieldTuples,
			ConditionOperators.All
		),
		otherCustomFieldsFilter: buildConditionStringArray(
			parsedFilters.otherCustomFieldTuples,
			ConditionOperators.All
		)
	};
};
